import { buildImageUrl } from "@/lib/lecturehub/images";
import { imageRemotePatterns, isRenderableImageSrc } from "@/lib/images/remote-patterns";
import type { Ott, OttDetail, OttVideo } from "@/lib/lecturehub/types";

const PATTERNS = imageRemotePatterns();

/**
 * OTT subtype helpers, shared by OttHero / OttView / OttDetailClient so the
 * three cannot disagree on "is this a series" or "how long is it".
 *
 * `videoType` is the OTT subtype (confusingly named — it is not the video file):
 * `"video"` is a single recording, `"simple_series"` / `"section_series"` are
 * episode lists (see docs/synced/portal-api/05-types.md). The public `Ott` type
 * has no section grouping, so a section series arrives as one flat `videos[]`.
 */
export function ottIsSeries(ott: Ott | null | undefined): boolean {
  return ott?.videoType === "simple_series" || ott?.videoType === "section_series";
}

/**
 * Total runtime in seconds. Takes the DETAIL shape: a series' runtime is the
 * sum of its episodes' durations, and a card only ever fetches episode ids, so
 * asking a card row this question is a type error rather than a silent 0.
 * `Ott.duration` is a computed field the detail fragment does not select, so
 * derive it: a single OTT is its `video`'s length
 * (trailer as a fallback so a locked module still shows a runtime), a series is
 * the sum of its episodes.
 */
export function ottTotalDuration(ott: OttDetail | null | undefined): number {
  if (!ott) return 0;
  if (ottIsSeries(ott)) {
    return (ott.videos ?? []).reduce((sum, v) => sum + (v.duration ?? v.video?.duration ?? 0), 0);
  }
  return ott.video?.duration ?? ott.trailer?.duration ?? 0;
}

/**
 * An episode poster `next/image` will actually render, or null. Goes through
 * buildImageUrl (CF variant when the tenant has Cloudflare Images) and the
 * allow-list predicate, exactly like InstructorCards: a raw `Image.uri` on an
 * unconfigured host THROWS at render in dev and took the series page down the
 * first time an episode carried art. Shared by the rail list and the strip so
 * the two cannot disagree.
 */
export function episodeThumb(v: OttVideo, cfHash: string | null): string | null {
  const raw = buildImageUrl(v.image ?? null, "public", cfHash);
  return raw && isRenderableImageSrc(raw, PATTERNS) ? raw : null;
}
